import { useMemo, useRef } from "react";
import type { Hint } from "~/features/chat/types";
import type { TermMatch, TermMatcher } from "~/utils/termMatcher";
import {
  buildTermRegex,
  computeTermMatches,
  TERM_HIGHLIGHT_CLASS,
} from "~/utils/termMatching";

/**
 * Escapes HTML special characters for safe injection into markup.
 */
function escapeHtml(str: string): string {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Escapes a string for use in an HTML attribute value.
 */
function escapeAttr(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Protected segments regex: code blocks, inline code, HTML tags, links */
const PROTECTED_RE =
  /(`{3,}[\s\S]*?`{3,}|`[^`]+`|<[^>]+>|\[[^\]]*\]\([^)]*\))/g;

/**
 * Splits content into protected and unprotected segments.
 * Protected segments (code, HTML, links) are never highlighted.
 */
function splitProtected(
  content: string
): Array<{ text: string; isProtected: boolean }> {
  const segments: { text: string; isProtected: boolean }[] = [];
  let lastIndex = 0;

  let match: RegExpExecArray | null;
  PROTECTED_RE.lastIndex = 0;
  while ((match = PROTECTED_RE.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        text: content.slice(lastIndex, match.index),
        isProtected: false,
      });
    }
    segments.push({ text: match[0], isProtected: true });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    segments.push({ text: content.slice(lastIndex), isProtected: false });
  }

  return segments;
}

/**
 * Injects <mark> tags at specified positions in text.
 */
function injectMarks(text: string, matches: TermMatch[]): string {
  if (!matches.length) return text;

  const parts: string[] = [];
  let cursor = 0;

  for (const m of matches) {
    if (m.start > cursor) {
      parts.push(text.slice(cursor, m.start));
    }
    const original = text.slice(m.start, m.end);
    const key = escapeAttr(m.term);
    const safeText = escapeHtml(original);
    parts.push(
      `<mark class="${TERM_HIGHLIGHT_CLASS}" data-term-key="${key}" tabindex="0" role="button">${safeText}</mark>`
    );
    cursor = m.end;
  }

  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }

  return parts.join("");
}

/**
 * Highlights terms in content using both regex (AI terms) and
 * Aho-Corasick (medications) matchers.
 */
function highlightContent(
  content: string,
  regex: RegExp | null,
  matcher: TermMatcher | null
): string {
  const segments = splitProtected(content);

  const processed = segments.map((segment) => {
    if (segment.isProtected) return segment.text;

    const merged = computeTermMatches(segment.text, regex, matcher);
    if (!merged.length) return segment.text;

    return injectMarks(segment.text, merged);
  });

  return processed.join("");
}

/**
 * Hook that injects `<mark>` tags around matched terms in markdown content.
 *
 * Supports two term sources:
 * - AI-extracted terms (small set, ~10-50): matched via single combined regex
 * - Medications dictionary (large set, ~9500): matched via Aho-Corasick O(n)
 *
 * AI term definitions take priority when overlapping with medications.
 *
 * The `<mark>` tags include a `data-term-key` attribute (lowercased term)
 * for lookup by the shared TermDefinitionDialog component via event delegation.
 */
export function useTermHighlighting(
  content: string,
  terms: Hint[],
  medicationMatcher?: TermMatcher | null
): string {
  const prevTermsRef = useRef<Hint[]>([]);
  const prevRegexRef = useRef<RegExp | null>(null);

  const regex = useMemo(() => {
    const prev = prevTermsRef.current;
    if (
      prev === terms ||
      (prev.length === terms.length &&
        prev.every((t, i) => t.term === terms[i].term))
    ) {
      return prevRegexRef.current;
    }
    prevTermsRef.current = terms;
    const built = buildTermRegex(terms);
    prevRegexRef.current = built;
    return built;
  }, [terms]);

  const matcher = medicationMatcher ?? null;

  return useMemo(() => {
    if (!regex && !matcher) return content;
    if (!content) return content;
    return highlightContent(content, regex, matcher);
  }, [content, regex, matcher]);
}
